/**
 * Rules: reveal, banners, chording, death, score. One class holds a run and applies actions.
 * Everything here is specified in spec/core-spec.md, section "Rules", and pinned by the rule vectors.
 */
import {
  COORD_LIMIT,
  type Coord,
  distanceSq,
  isValidCoord,
  keyToCoord,
  NEIGHBOURS,
  tileKey,
} from "./coords.ts";
import { metresFromDistanceSq } from "./distance.ts";
import type { Action, Mode, RunRecord } from "./record.ts";
import {
  adjacentChasms,
  DEFAULT_WORLD,
  isChasm,
  type Terrain,
  terrainAt,
  WORLD_VERSION,
  type WorldConfig,
} from "./world.ts";

export type Status = "playing" | "dead";

/** At most this many tiles are revealed by one action, in breadth-first order. */
export const FLOOD_CAP = 6000;

export interface ActionResult {
  /** False when the action did nothing and was not recorded. */
  readonly changed: boolean;
  /** Tiles revealed by this action, in the order they were revealed. */
  readonly revealed: readonly Coord[];
  /** True when this action ended the run. */
  readonly died: boolean;
}

const NO_CHANGE: ActionResult = { changed: false, revealed: [], died: false };

export interface GameOptions {
  readonly seed: number;
  readonly mode: Mode;
  readonly day?: number | null;
  readonly startedAt?: number;
  readonly config?: WorldConfig;
  /** How many members the party has, 1 to PARTY_MAX; 1 when absent (spec 6.8). */
  readonly party?: number;
}

/** The most members a party may have (spec 6.8, decision 0043). */
export const PARTY_MAX = 6;

/** A member's fall: who fell, and on which chasm. */
export interface Fall {
  readonly member: number;
  readonly x: number;
  readonly y: number;
}

export interface RunSummary {
  readonly status: Status;
  readonly killer: Coord | null;
  readonly tiles: number;
  readonly banners: number;
  readonly maxDistanceSq: number;
  readonly metres: number;
}

export class Game {
  readonly seed: number;
  readonly mode: Mode;
  readonly day: number | null;
  readonly startedAt: number;
  readonly config: WorldConfig;

  private readonly revealedSet = new Set<number>();
  private readonly bannerSet = new Set<number>();
  private readonly adjacentCache = new Map<number, number>();
  private readonly terrainCache = new Map<number, Terrain>();
  private statusValue: Status = "playing";
  private killerValue: Coord | null = null;
  private maxDistanceSqValue = 0;
  private actionList: Action[] = [];
  private partyValue = 1;
  /** The chasms members fell into: hidden, and counted as banners by every rule (spec 6.8). */
  private readonly fallenSet = new Set<number>();
  private readonly outSet = new Set<number>();
  private readonly fallList: Fall[] = [];

  private constructor(options: GameOptions) {
    this.seed = options.seed >>> 0;
    this.mode = options.mode;
    this.day = options.day ?? null;
    this.startedAt = options.startedAt ?? 0;
    this.config = options.config ?? DEFAULT_WORLD;
    const limit = this.config.coordLimit ?? COORD_LIMIT;
    if (!Number.isInteger(limit) || limit < 1 || limit > COORD_LIMIT) {
      throw new Error(`coordLimit must be an integer in [1, ${COORD_LIMIT}]`);
    }
    if (this.config.chasms?.some(([x, y]) => x === 0 && y === 0)) {
      throw new Error("chasms never lists the cabin's tile");
    }
    const party = options.party ?? 1;
    if (!Number.isInteger(party) || party < 1 || party > PARTY_MAX) {
      throw new Error(`party must be an integer in [1, ${PARTY_MAX}]`);
    }
    this.partyValue = party;
  }

  /** Starts a run. The cabin tile at the origin is revealed immediately; that reveal is not an action. */
  static create(options: GameOptions): Game {
    const game = new Game(options);
    game.revealFrom(0, 0, { left: FLOOD_CAP }, []);
    return game;
  }

  /** Rebuilds a run by replaying its record. The result's record equals the input. */
  static fromRecord(record: RunRecord, config?: WorldConfig): Game {
    const options: GameOptions = {
      seed: record.seed,
      mode: record.mode,
      day: record.day,
      startedAt: record.startedAt,
      ...(config ? { config } : {}),
      ...(record.party !== undefined ? { party: record.party } : {}),
    };
    const game = Game.create(options);
    for (const action of record.actions) game.apply(action);
    game.actionList = [...record.actions];
    return game;
  }

  get status(): Status {
    return this.statusValue;
  }

  get killer(): Coord | null {
    return this.killerValue;
  }

  /** Squared tile distance of the furthest revealed tile. */
  get maxDistanceSq(): number {
    return this.maxDistanceSqValue;
  }

  get metres(): number {
    return metresFromDistanceSq(this.maxDistanceSqValue);
  }

  /** Number of revealed tiles, including the cabin. */
  get tiles(): number {
    return this.revealedSet.size;
  }

  get banners(): number {
    return this.bannerSet.size;
  }

  get actions(): readonly Action[] {
    return this.actionList;
  }

  get record(): RunRecord {
    return {
      v: 1,
      world: WORLD_VERSION,
      mode: this.mode,
      seed: this.seed,
      day: this.day,
      startedAt: this.startedAt,
      ...(this.partyValue > 1 ? { party: this.partyValue } : {}),
      actions: [...this.actionList],
    };
  }

  /** How many members the party has; 1 for a run played alone. */
  get party(): number {
    return this.partyValue;
  }

  /** The falls so far, in the order they happened. */
  get falls(): readonly Fall[] {
    return this.fallList;
  }

  /** True when the member has fallen; such a member's actions are no-ops (spec 6.8). */
  hasFallen(member: number): boolean {
    return this.fallList.some((f) => f.member === member);
  }

  /** True when the host took the member out; such a member's actions are no-ops (spec 6.8). */
  isOut(member: number): boolean {
    return this.outSet.has(member);
  }

  /** True when a member fell into the chasm on this tile and the run went on (spec 6.8). */
  isFallen(x: number, y: number): boolean {
    return this.fallenSet.has(tileKey(x, y));
  }

  /**
   * A member joins the party while the run is playing and the party has room. Returns the new
   * member's index, or null. Not an action: the record's `party` carries it (spec 6.8).
   */
  join(): number | null {
    if (this.statusValue !== "playing" || this.partyValue >= PARTY_MAX) return null;
    this.partyValue += 1;
    return this.partyValue - 1;
  }

  get summary(): RunSummary {
    return {
      status: this.statusValue,
      killer: this.killerValue,
      tiles: this.tiles,
      banners: this.banners,
      maxDistanceSq: this.maxDistanceSqValue,
      metres: this.metres,
    };
  }

  isRevealed(x: number, y: number): boolean {
    return this.revealedSet.has(tileKey(x, y));
  }

  hasBanner(x: number, y: number): boolean {
    return this.bannerSet.has(tileKey(x, y));
  }

  isChasm(x: number, y: number): boolean {
    return isChasm(this.seed, x, y, this.config);
  }

  adjacent(x: number, y: number): number {
    const key = tileKey(x, y);
    const cached = this.adjacentCache.get(key);
    if (cached !== undefined) return cached;
    const n = adjacentChasms(this.seed, x, y, this.config);
    this.adjacentCache.set(key, n);
    return n;
  }

  terrain(x: number, y: number): Terrain {
    const key = tileKey(x, y);
    const cached = this.terrainCache.get(key);
    if (cached !== undefined) return cached;
    const t = terrainAt(this.seed, x, y);
    this.terrainCache.set(key, t);
    return t;
  }

  /** All revealed tiles, in no particular order. */
  revealedTiles(): Coord[] {
    const out: Coord[] = [];
    for (const key of this.revealedSet) out.push(keyToCoord(key));
    return out;
  }

  /** All banners, in no particular order. */
  bannerTiles(): Coord[] {
    const out: Coord[] = [];
    for (const key of this.bannerSet) out.push(keyToCoord(key));
    return out;
  }

  /**
   * A revealed number is closed when every hidden neighbour carries a banner and the banners
   * number exactly the adjacent chasms. Revealed neighbours are never chasms while the run
   * is alive, so the banners of a closed number are exactly its chasms: the number says
   * nothing more and renderers may hide it. Spec 6.7.
   */
  isClosed(x: number, y: number): boolean {
    if (!this.revealedSet.has(tileKey(x, y))) return false;
    const n = this.adjacent(x, y);
    if (n === 0) return false;
    let banners = 0;
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!this.isValid(nx, ny)) continue;
      const key = tileKey(nx, ny);
      if (this.revealedSet.has(key)) continue;
      if (!this.isMarked(key)) return false;
      banners += 1;
    }
    return banners === n;
  }

  /** A bannered tile is settled when a neighbouring number is closed: it is a proven chasm. */
  isSettled(x: number, y: number): boolean {
    if (!this.bannerSet.has(tileKey(x, y))) return false;
    for (const [dx, dy] of NEIGHBOURS) if (this.isClosed(x + dx, y + dy)) return true;
    return false;
  }

  /**
   * Reveals a hidden tile without a banner that touches revealed ground. A chasm ends the run;
   * otherwise the flood fill runs. A tile that touches no revealed tile is out of reach: the
   * player walks from the cabin, so the revealed set stays connected (spec 6.4).
   */
  /** Takes one action of a record, in its member's name. */
  apply(action: Action): ActionResult {
    const [kind, x, y] = action;
    const member = action[3] ?? 0;
    if (kind === "r") return this.reveal(x, y, member);
    if (kind === "f") return this.toggleBanner(x, y, member);
    if (kind === "c") return this.chord(x, y, member);
    return this.takeOut(member);
  }

  reveal(x: number, y: number, member = 0): ActionResult {
    if (!this.mayAct(member) || !this.isValid(x, y)) return NO_CHANGE;
    const key = tileKey(x, y);
    if (this.revealedSet.has(key) || this.isMarked(key)) return NO_CHANGE;
    if (!this.touchesRevealed(x, y)) return NO_CHANGE;
    this.actionList.push(action("r", x, y, member));
    if (this.isChasm(x, y)) {
      return { changed: true, revealed: [], died: this.fall(member, x, y) };
    }
    const revealed: Coord[] = [];
    this.revealFrom(x, y, { left: FLOOD_CAP }, revealed);
    return { changed: true, revealed, died: false };
  }

  /** Places or removes a banner on a hidden tile that touches revealed ground. */
  toggleBanner(x: number, y: number, member = 0): ActionResult {
    if (!this.mayAct(member) || !this.isValid(x, y)) return NO_CHANGE;
    const key = tileKey(x, y);
    if (this.revealedSet.has(key) || this.fallenSet.has(key)) return NO_CHANGE;
    if (!this.touchesRevealed(x, y)) return NO_CHANGE;
    this.actionList.push(action("f", x, y, member));
    if (this.bannerSet.has(key)) this.bannerSet.delete(key);
    else this.bannerSet.add(key);
    return { changed: true, revealed: [], died: false };
  }

  /**
   * Chords a revealed number whose banner count matches: reveals each hidden neighbour without
   * a banner, in neighbour order, sharing one flood budget. A wrongly bannered chasm among
   * them ends the run at the first chasm met. Neighbours outside the valid range are skipped.
   */
  chord(x: number, y: number, member = 0): ActionResult {
    if (!this.mayAct(member) || !this.isValid(x, y)) return NO_CHANGE;
    if (!this.revealedSet.has(tileKey(x, y))) return NO_CHANGE;
    const n = this.adjacent(x, y);
    if (n === 0) return NO_CHANGE;
    let banners = 0;
    let hidden = 0;
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!this.isValid(nx, ny)) continue;
      const key = tileKey(nx, ny);
      if (this.isMarked(key)) banners += 1;
      else if (!this.revealedSet.has(key)) hidden += 1;
    }
    if (banners !== n || hidden === 0) return NO_CHANGE;
    this.actionList.push(action("c", x, y, member));
    const budget = { left: FLOOD_CAP };
    const revealed: Coord[] = [];
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = x + dx;
      const ny = y + dy;
      if (!this.isValid(nx, ny)) continue;
      const key = tileKey(nx, ny);
      if (this.revealedSet.has(key) || this.isMarked(key)) continue;
      if (this.isChasm(nx, ny)) {
        return { changed: true, revealed, died: this.fall(member, nx, ny) };
      }
      this.revealFrom(nx, ny, budget, revealed);
    }
    return { changed: true, revealed, died: false };
  }

  /**
   * A member falls into the chasm at (x, y) and the killer is the latest fall. The run ends when
   * every member has fallen or been taken out; otherwise the tile is marked fallen and play goes
   * on. The fall that ends the run marks nothing, so a run alone ends exactly as it always did.
   * Returns true when the run ended.
   */
  private fall(member: number, x: number, y: number): boolean {
    this.fallList.push({ member, x, y });
    this.killerValue = { x, y };
    if (!this.nobodyLeft()) {
      this.fallenSet.add(tileKey(x, y));
      return false;
    }
    this.statusValue = "dead";
    return true;
  }

  /**
   * The host takes a member out of the party (spec 6.8). What they found stays found, and the run
   * ends if that leaves nobody out in the mist, as when the last member falls.
   */
  takeOut(member: number): ActionResult {
    if (this.statusValue !== "playing") return NO_CHANGE;
    if (!Number.isInteger(member) || member < 1 || member >= this.partyValue) return NO_CHANGE;
    if (this.outSet.has(member)) return NO_CHANGE;
    this.outSet.add(member);
    this.actionList.push(action("o", 0, 0, member));
    const died = this.nobodyLeft();
    if (died) this.statusValue = "dead";
    return { changed: true, revealed: [], died };
  }

  /** Every member has fallen or been taken out. */
  private nobodyLeft(): boolean {
    for (let m = 0; m < this.partyValue; m += 1) {
      if (!this.hasFallen(m) && !this.outSet.has(m)) return false;
    }
    return true;
  }

  /** The run is playing and the member is in the party and has not fallen. */
  private mayAct(member: number): boolean {
    if (this.statusValue !== "playing") return false;
    if (!Number.isInteger(member) || member < 0 || member >= this.partyValue) return false;
    return !this.hasFallen(member) && !this.outSet.has(member);
  }

  /** Bannered, or a chasm a member fell into: every rule counts both as banners (spec 6.8). */
  private isMarked(key: number): boolean {
    return this.bannerSet.has(key) || this.fallenSet.has(key);
  }

  /** Validity under this run's coordinate limit (spec 3 and 4.1). */
  private isValid(x: number, y: number): boolean {
    return isValidCoord(x, y, this.config.coordLimit ?? COORD_LIMIT);
  }

  /** True when at least one of the eight neighbours is revealed: the tile is within reach. */
  private touchesRevealed(x: number, y: number): boolean {
    for (const [dx, dy] of NEIGHBOURS) {
      if (this.revealedSet.has(tileKey(x + dx, y + dy))) return true;
    }
    return false;
  }

  /**
   * Breadth-first flood from a tile known not to hold a chasm. Reveals the tile, and from every
   * revealed tile with no adjacent chasms continues to its hidden neighbours without banners,
   * in neighbour order, until the budget is spent. Validity is checked when a tile is popped, so
   * the flood never reveals an invalid tile, including an invalid start.
   */
  private revealFrom(x0: number, y0: number, budget: { left: number }, out: Coord[]): void {
    const queue: number[] = [x0, y0];
    let head = 0;
    while (head < queue.length && budget.left > 0) {
      const x = queue[head] as number;
      const y = queue[head + 1] as number;
      head += 2;
      if (!this.isValid(x, y)) continue;
      const key = tileKey(x, y);
      if (this.revealedSet.has(key) || this.isMarked(key)) continue;
      this.revealedSet.add(key);
      budget.left -= 1;
      out.push({ x, y });
      const d2 = distanceSq(x, y);
      if (d2 > this.maxDistanceSqValue) this.maxDistanceSqValue = d2;
      if (this.adjacent(x, y) !== 0) continue;
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = x + dx;
        const ny = y + dy;
        const nk = tileKey(nx, ny);
        if (!this.revealedSet.has(nk) && !this.isMarked(nk)) queue.push(nx, ny);
      }
    }
  }
}

/** An action as recorded: the member is written only when it is not the first (spec 6.8). */
function action(kind: Action[0], x: number, y: number, member: number): Action {
  return member === 0 ? [kind, x, y] : [kind, x, y, member];
}
